import { computed, ComputedRef, reactive, readonly, ref, Ref, unref, watch } from 'vue'
import type { DeepReadonly } from 'vue'
import {
  ChildTimer,
  ChildTimers,
  SyncTimers,
  SyncTimesheet,
  Timer,
  TimerData,
  TimerHistory,
  Timesheet,
} from '../types/timers'
import { useAuthenticateSAML } from '../store/useAuthenticateSAML'
import { defineStore, storeToRefs } from 'pinia'
import { TimersModel } from '../models/TimersModel'
import { useBackgroundGeolocation } from '../composables/backgroundGeolocation'
import { MaybeRef } from '@vueuse/core'
import { v4 as uuidv4 } from 'uuid'
import { format, formatISO } from 'date-fns'
import { useExpenses } from './useExpenses'
import { useTimesheets } from './timesheets'
import { useFormatDate } from '../composables/formatDate'
import { useI18n } from 'vue-i18n'
import { useUserStore } from './useUserStore'
import { mapTimersForSync } from '../synchronizations/helpers/timersSync'
import { workingDate } from '../App.vue'
import { Coordinates } from 'app/types/coordinates'
import { addBreadcrumb, captureException } from '@sentry/vue'

type UseTimersReturn = {
  resetStoreState: () => void
  stopActiveParentTimer: (options?: { date?: Date }) => void
  addParentTimer: (data: any, date?: Date, timerRef?: Timer[]) => Promise<void>
  insertNewTimer: (data: {
    start_time: Date
    end_time: Date
    type: Timer['type']
    data?: any
  }) => Promise<void>
  removeParent: (index: number) => void
  removeChildByDataField: (key: string, value: string | number | boolean) => void
  getAllParentTimers: DeepReadonly<ComputedRef<Timer[]>>
  /**gets data, but with dates formatted as ISO strings w/ timezones */
  getTimeSheet: () => Omit<SyncTimesheet, 'agree_legal' | 'claim_travel_override'> | null
  getActiveParentTimer: ComputedRef<Timer | null>
  storeActive: ComputedRef<boolean>
  endParentTimer: () => void
  editParentData: (index: number, data: { [key: string]: MaybeRef<any> }) => void
  editParentStartTime: (newTime: Date, index: number) => void
  editParentEndTime: (newTime: Date, index: number) => void
  stopActiveChildTimer: (date?: Date) => void
  editChildData: (
    parentIndex: number,
    childIndex: number,
    data: { [key: string]: MaybeRef<any> }
  ) => void
  editChildStartTime: (newTime: Date, childIndex: number, parentIndex: number) => void
  editChildEndTime: (newTime: Date, childIndex: number, parentIndex: number) => void
  getActiveChildTimer: ComputedRef<ChildTimer | null>
  addChildTimer: (data: any, date?: Date) => Promise<void>
  clearAllTimers: (options?: {
    resetSwap?: boolean
    clearOriginal?: boolean
    skipWrite?: boolean
  }) => void
  breakActive: ComputedRef<boolean>
  startBreak: (options?: { code?: number; date?: Date; data?: { [key: string]: any } }) => void
  endBreak: (options?: { date: Date }) => void
  startDriving: (options?: {
    odometer_start?: MaybeRef<number | string>
    odometer_end?: MaybeRef<number | string>
    date?: Date
  }) => void
  endDriving: (options?: { date?: Date; odometer?: MaybeRef<number | string> }) => void
  calcTimerTime: (timer: Timer | ChildTimer) => number
  getTotalParentTimes: ComputedRef<number>
  getTotalTime: (id: Timer['id']) => number
  getTotalBreak: ComputedRef<number>
  getCurrentBreakTime: ComputedRef<number>
  getCurrentParentTime: ComputedRef<number>
  getCurrentChildTime: ComputedRef<number>
  sortTimers: () => void
  lockTimerEdits: (timerRef?: Timer[]) => void
  unlockTimerEdits: () => void
  isLocked: ComputedRef<boolean>
  timeOverlaps: (data: { date: Date; index?: number }) => boolean
  timerConflict: (data: any, timerRef?: Timer[] | ChildTimers) => boolean
  canEdit: any
  /** swap is used when editing older timecards, unlock them if reps can */
  setTimers: (data: Timer[], swap?: boolean, unlock?: boolean, clear?: boolean) => Promise<void>
  resetOriginalTimers: (index: number) => void
  setOriginalTimers: (data: Timer[]) => void
  editParentStartEnd: (
    newTimes: {
      start_time: Timer['start_time']
      end_time: NonNullable<Timer['end_time']>
    },
    parentIndex: number
  ) => void
  editChildStartEnd: (
    newTimes: {
      start_time: Timer['start_time']
      end_time: NonNullable<Timer['end_time']>
    },
    parentIndex: number,
    childIndex: number
  ) => void
  /**returns incomplete timesheet, if no activities are logged, returns null */
  getIncompleteTimeSheet: ComputedRef<Timesheet | null>
  /**if timers are in a swapped state or not */
  swappedTimers: Ref<boolean>
}
const { writeTimers } = TimersModel()
const timers: Ref<Timer[]> = ref([])
const swappedTimers = ref(false)
let originalTimers = reactive<Timer[]>([])

export const useTimers = defineStore('timers', (): UseTimersReturn => {
  watch(
    timers,
    async (newData) => {
      if (!swappedTimers.value) {
        if (!skip.value) {
          await writeTimers([...newData], workingDate.value)
        } else {
          skip.value = false
        }
      } else {
        console.log('swapped timers, not writing')
      }
    },
    { deep: true }
  )
  const resetStoreState = () => {
    skip.value = true
    timers.value = []
    swappedTimers.value = false
    originalTimers = reactive([])
  }
  const skip = ref(false)

  /*
   * Swap timesheets
   */
  const { updateTimesheet } = useTimesheets()
  const resetOriginalTimers = async (index: number) => {
    updateTimesheet(index, [...timers.value])
    timers.value = []
    await setTimers([...originalTimers])
    originalTimers = []
    swappedTimers.value = false
  }
  const setOriginalTimers = (data: Timer[]) => {
    originalTimers = data
  }
  const setTimers = async (data: Timer[], swap = false, unlock = false, clear = false) => {
    //skip if already in a swapped state
    if (swap && swappedTimers.value === true) {
      return void 0
    }
    if (swap && swappedTimers.value === false) {
      swappedTimers.value = true
      setOriginalTimers(Object.assign([...timers.value]))
      timers.value = []
    }
    if (unlock) {
      unlockTimerEdits(data)
    }
    if (clear) {
      timers.value = []
    }

    const out: Timer[] = []
    for (const parent of data) {
      await addParentTimer(parent, undefined, out).catch((error) => {
        console.trace()
        console.error(error)
        addBreadcrumb({
          category: 'Error setting parent timer',
          message: error,
          level: 'debug',
          data: { parentTimer: parent },
        })
        captureException(error)
        })
    }
    timers.value = out
  }
  /*
   * Parent timer functions
   */
  const stopActiveParentTimer = (options?: { date?: Date }): void => {
    if (getActiveParentTimer.value) {
      stopActiveChildTimer(options?.date)

      endTimer(getActiveParentTimer.value, options?.date)
    }
  }
  const addParentTimer = async (data: any, date?: Date, timerRef?: Timer[]): Promise<void> => {
    timerRef = timerRef ?? timers.value
    stopActiveParentTimer({ date: date })
    const childrenData = data.children ? data.children : []
    data = { ...data, children: childrenData }
    await createTimer(data, timerRef, date)
  }

  const insertNewTimer = async (data: {
    start_time: Date
    end_time: Date
    type: Timer['type']
    data?: any
  }) => {
    if (!timerConflict({ start_time: data.start_time, end_time: data.end_time })) {
      const { start_time, end_time, type, ...rest } = data
      await createTimer(
        {
          start_time: new Date(start_time),
          end_time: new Date(end_time),
          active: false,
          type: type,
          children: [],
          ...rest,
          data: {
            history: {
              _created_at: new Date(),
              _staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
            },
            ...rest.data,
          },
        },
        timers.value
      ).catch((error) => console.log(error))
    }
  }

  const removeParent = (index: number) => {
    timers.value.splice(index, 1)
  }

  const removeChildByDataField = (key: string, value: string | number | boolean) => {
    timers.value = timers.value.map((timer) => {
      console.log(timer)
      timer.children = timer.children.filter((child) => {
        return child.data[key] != value
      })
      console.log(timer)
      return timer
    })
  }

  const getActiveParentTimer = computed<Timer | null>(() => {
    if (timers.value.length == 0) return null
    const activeTimer = timers.value.find((timer) => timer.active)
    if (activeTimer === undefined) return null
    else return activeTimer
  })
  const storeActive = computed(() => {
    if (getActiveParentTimer.value && getActiveParentTimer.value.type == 'store') return true
    else return false
  })
  const endParentTimer = (): void => {
    const activeTimer = getActiveParentTimer.value
    if (activeTimer) {
      stopActiveChildTimer()
      endTimer(activeTimer)
    }
  }
  const editParentStartTime = (newTime: Date, index: number) => {
    const parent: Timer = timers.value[index]
    // const firstChild = parent.children[0].start_time
    if (canEditParentStart(newTime, index)) {
      parent.start_time = newTime
      return
    }
  }
  const canEditParentStart = (newTime: Date, parentIndex: number): boolean => {
    const parent: Timer = timers.value[parentIndex]
    const lastParent: Timer = timers.value[parentIndex - 1]
    if (
      parent.editable &&
      (!parent.end_time || parent.start_time <= parent.end_time!) &&
      (!lastParent || newTime >= lastParent.end_time!) &&
      (parent.children.length < 1 || newTime <= parent.children[0].start_time)
    ) {
      return true
    } else return false
  }
  const editParentEndTime = (newTime: Date, index: number) => {
    const parent: Timer = timers.value[index]
    if (canEditParentEnd(newTime, index)) {
      parent.end_time = newTime
    }
  }
  const canEditParentEnd = (newTime: Date, parentIndex: number): boolean => {
    const parent: Timer = timers.value[parentIndex]
    const nextParent: Timer = timers.value[parentIndex + 1]
    const lastChild = parent.children[parent.children.length - 1]
    if (
      parent.editable &&
      (parent.children.length < 1 ||
        (parent.children && lastChild && lastChild.end_time && newTime >= lastChild.end_time)) &&
      newTime >= parent.start_time &&
      (typeof nextParent == 'undefined' || newTime <= nextParent.start_time)
    ) {
      return true
    } else return false
  }
  const editParentData = (index: number, data: { [key: string]: MaybeRef<any> }) => {
    const parent = timers.value[index]
    //overwrite any value present in input data w/o delete other fields
    for (const key in data) {
      parent.data[key] = unref(data[key])
    }
    addTimerHistory(parent)
  }
  /*
   * Child timer functions
   */
  const stopActiveChildTimer = (date?: Date) => {
    if (getActiveChildTimer.value) {
      endTimer(getActiveChildTimer.value, date)
    }
  }
  const editChildStartTime = (newTime: Date, parentIndex: number, childIndex: number) => {
    const child: ChildTimer = timers.value[parentIndex].children[childIndex]
    if (canEditChildStart(newTime, parentIndex, childIndex)) {
      child.start_time = newTime
    }
  }
  const canEditChildStart = (newTime: Date, parentIndex: number, childIndex: number) => {
    const parent = timers.value[parentIndex]
    const child = parent.children[childIndex]
    const previousChild = parent.children[childIndex - 1]
    if (
      child.editable &&
      newTime >= parent.start_time &&
      (!previousChild || previousChild.end_time! <= newTime)
    ) {
      return true
    } else {
      return false
    }
  }
  const editChildEndTime = (newTime: Date, parentIndex: number, childIndex: number) => {
    const child: ChildTimer = timers.value[parentIndex].children[childIndex]
    if (canEditChildEnd(newTime, parentIndex, childIndex)) {
      child.end_time = newTime
    }
  }
  const canEditChildEnd = (newTime: Date, parentIndex: number, childIndex: number) => {
    const parent = timers.value[parentIndex]
    const child = parent.children[childIndex]
    const nextChild = parent.children[childIndex + 1]
    if (!child.end_time) return false
    if (
      child.editable &&
      ((!parent.end_time && newTime < new Date()) || newTime <= parent.end_time!) &&
      (!nextChild || newTime <= nextChild.start_time)
    ) {
      return true
    } else {
      return false
    }
  }
  const getActiveChildTimer = computed((): ChildTimer | null => {
    if (getActiveParentTimer.value === null) {
      return null
    }
    const activeChildTimer = getActiveParentTimer.value.children.find((child) => child.active)
    if (activeChildTimer === undefined) return null
    return activeChildTimer
  })

  const addChildTimer = async (data: any, date?: Date) => {
    const parentTimer = getActiveParentTimer.value
    if (parentTimer != undefined && 'children' in parentTimer) {
      stopActiveChildTimer(date)
      await createTimer({ ...data }, parentTimer.children, date).catch((error) =>
        console.log(error)
      )
    }
  }
  /*
   * Various all Timers
   */
  const endTimer = (timer: Timer | ChildTimer, date?: Date): void => {
    const currentTime = new Date()
    if (!timer.data.history) {
      timer.data.history = {
        _created_at: new Date(),
        _staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
      }
    }
    timer.data.history._closed_at = new Date()
    timer.end_time = date ? date : currentTime
    timer.active = false
  }
  const createTimer = async (data: any, timerRef: Timer[] | ChildTimers, date?: Date) => {
    const currentTime = date ? date : new Date()
    const {
      returnTo: returnTo,
      active: parentActive,
      id: parentID,
      uuid: parentUUID,
      name: parentName,
      type: parentType,
      start_time: parentStart,
      end_time: parentEnd,
      editable: parentEditable,
      coordinates: parentCoordinates,
      children: parentChildren,
      data: parentData,
      ...otherData
    }: TimerData = data
    let longitude
    let latitude
    let accuracy
    if (
      parentCoordinates &&
      parentCoordinates.lat &&
      parentCoordinates.lng &&
      parentCoordinates.accuracy
    ) {
      longitude = parentCoordinates.lng
      latitude = parentCoordinates.lat
      accuracy = parentCoordinates.accuracy
    }
    const startValue = parentStart ? new Date(parentStart) : currentTime
    const endValue = parentEnd ? new Date(parentEnd) : null
    const editableValue: boolean = typeof parentEditable !== 'undefined' ? parentEditable : true

    let childrenData = []
    if (parentChildren) {
      childrenData = parentChildren.map((child: any) => {
        return {
          id: child.id ? child.id : parentChildren.length,
          uuid: child.uuid ? child.uuid : uuidv4(),
          active: child.active,
          name: child.name,
          start_time: child.start_time ? new Date(child.start_time) : currentTime,
          end_time: child.end_time ? new Date(child.end_time) : null,
          editable: child.editable,
          coordinates: child.coordinates,
          type: child.type,
          data: child.data,
        }
      })
    }

    timerRef.push({
      id: parentID ? parentID : timers.value.length,
      uuid: parentUUID ? parentUUID : uuidv4(),
      active: typeof parentActive !== 'undefined' ? parentActive : true,
      editable: editableValue,
      start_time: startValue,
      end_time: endValue,
      type: parentType,
      name: parentName,
      data: { ...parentData, ...otherData },
      returnTo: returnTo,
      coordinates: {
        lat: latitude,
        lng: longitude,
        accuracy: accuracy,
      },
      ...(parentChildren && { children: [...childrenData] }),
    })
    //if no history, we've created a brand new timer and should stamp it with its real creation date
    if (!timerRef[timerRef.length - 1].data.history) {
      timerRef[timerRef.length - 1].data.history = {
        _created_at: new Date(),
        _staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
      } as TimerHistory
    }
    if (!parentCoordinates || parentCoordinates.lat === null) {
      setTimeout(async () => {
        await assignCoordinates(timerRef[timerRef.length - 1]).catch((error) =>
          console.log(`Error assigning coordinates: ${error}`)
        )
      })
    }
    if (parentType == 'travel') {
      if (!parentData.start) {
        setTimeout(async () => {
          await assignStart(timerRef[timerRef.length - 1])
        })
      }
      if (!parentData.end && parentData.incomplete === false) {
        setTimeout(async () => {
          await assignEnd(timerRef[timerRef.length - 1])
        })
      }
    }

    return timerRef
  }

  const sortTimers = () => {
    timers.value.sort((a, b) => {
      return a.start_time.getTime() - b.start_time.getTime()
    })
  }
  const returnLatLngAcc = async (): Promise<Coordinates | never[]> => {
    const { getGPSPosition } = useBackgroundGeolocation()
    const data = await getGPSPosition().catch((error) => console.log(error))
    if (data) {
      return {
        lat: data.latitude,
        lng: data.longitude,
        accuracy: data.accuracy,
      }
    }
    return []
  }
  const assignCoordinates = async (timerRef: Timer | ChildTimer) => {
    if (!Array.isArray(timerRef.coordinates) && timerRef.coordinates.lat == null) {
      timerRef.coordinates = await returnLatLngAcc()
    }
  }
  const assignStart = async (timerRef: Timer | ChildTimer) => {
    if (timerRef.data.start == null) {
      timerRef.data.start = await returnLatLngAcc()
    }
  }
  const assignEnd = async (timerRef: Timer | ChildTimer) => {
    if (timerRef.data.end == null) {
      timerRef.data.end = await returnLatLngAcc()
    }
  }

  // Option to allow us to reset swapped state so we can save.
  const clearAllTimers = (options?: {
    resetSwap?: boolean
    clearOriginal?: boolean
    skipWrite?: boolean
  }) => {
    if (options) {
      if (options.resetSwap) swappedTimers.value = false
    }
    if (options && options.clearOriginal) {
      originalTimers = []
    } else {
      if (options && options.skipWrite) {
        skip.value = true
      }
      timers.value = []
    }
  }
  const lockTimerEdits = (timerRef = timers.value) => {
    mapTimerEdits(false, timerRef)
  }
  const unlockTimerEdits = (timerRef = timers.value) => {
    mapTimerEdits(true, timerRef)
  }
  const mapTimerEdits = (editFlag: boolean, timerRef = timers.value) => {
    timerRef.forEach((timer) => {
      timer.editable = editFlag
      timer.children?.forEach((children) => {
        children.editable = editFlag
      })
    })
  }
  const isLocked = computed(() => {
    if (timers.value && timers.value.length > 0) {
      return !timers.value[0].editable
    } else {
      return false
    }
  })

  /*
   * Break
   */
  const startBreak = (options?: { code?: number; date?: Date; data?: { [key: string]: any } }) => {
    let activeID = null
    if (getActiveParentTimer.value) {
      activeID = getActiveParentTimer.value.data.store_id
    }
    stopActiveParentTimer({ date: options?.date })
    const data = {
      type: 'break',
      code: options?.code,
      data: options?.data,
      ...(activeID && { returnTo: activeID }),
    }
    void addParentTimer(data, options?.date).catch((error) => console.log(error))
  }
  const breakActive = computed<boolean>(() => getActiveParentTimer.value?.type == 'break')
  const getReturnTo = computed(() => {
    if (getActiveParentTimer.value && typeof getActiveParentTimer.value.returnTo != 'undefined') {
      return timers.value.find(
        (timer) => timer.data.store_id === getActiveParentTimer.value!.returnTo
      )
    } else return null
  })
  const endBreak = (options?: { date: Date }) => {
    if (getReturnTo.value && breakActive.value) {
      void addParentTimer({
        id: getReturnTo.value?.id,
        name: getReturnTo.value?.name,
        type: getReturnTo.value?.type,
        ...getReturnTo.value?.data,
      })
    } else {
      if (options && options.date) {
        stopActiveParentTimer({ date: options?.date })
      }
      stopActiveParentTimer({ date: options?.date })
    }
  }

  /*
   * Driving
   */
  const startDriving = async (options?: {
    odometer_start?: MaybeRef<number | string>
    odometer_end?: MaybeRef<number | string>
    date?: Date
  }) => {
    const { getWatcherData } = useBackgroundGeolocation()
    stopActiveParentTimer({ date: options?.date })
    // addWatcher()
    const data = {
      type: 'travel',
      location: getWatcherData.value,
      data: {
        odometer_start: unref(options?.odometer_start),
        odometer_end: unref(options?.odometer_end),
      },
    }
    void addParentTimer(data, options?.date)
  }
  const endDriving = async (options?: { odometer?: MaybeRef<number | string>; date?: Date }) => {
    // removeWatcher()
    if (getActiveParentTimer.value) {
      getActiveParentTimer.value.data.odometer_end = unref(options?.odometer)
      //clean up travel data

      void assignEnd(getActiveParentTimer.value)
      getActiveParentTimer.value.data.location = undefined
    }
    stopActiveParentTimer({ date: options?.date })
    //clean
  }

  /*
   * Time
   */
  const differenceInMinutes = (first: number, second: number) => (first - second) / 1000 / 60
  const calcTimerTime = (timer: Timer | ChildTimer): number => {
    const now = ref<Date>(new Date())
    setInterval(() => (now.value = new Date()), 1000)
    if (!timer.end_time) {
      return differenceInMinutes(now.value.getTime(), timer.start_time.getTime())
    } else {
      return differenceInMinutes(timer.end_time.getTime(), timer.start_time.getTime())
    }
  }
  const getTotalParentTimes = computed(() => {
    return timers.value.reduce((previousValue, currentValue) => {
      return previousValue + calcTimerTime(currentValue)
    }, 0)
  })
  const getTotalTime = (id: Timer['id']) => {
    return timers.value.reduce((previousValue, currentValue) => {
      if (currentValue.id === id) return previousValue + calcTimerTime(currentValue)
      else return previousValue
    }, 0)
  }
  const getTotalBreak = computed((): number => {
    return timers.value.reduce((previousValue, currentValue) => {
      if (currentValue.type !== 'break') {
        return previousValue
      }
      return previousValue + calcTimerTime(currentValue)
    }, 0)
  })
  const getCurrentBreakTime = computed((): number => {
    if (!getActiveParentTimer.value || getActiveParentTimer.value.type != 'break') return 0
    return calcTimerTime(getActiveParentTimer.value)
  })
  const getCurrentParentTime = computed((): number => {
    if (!getActiveParentTimer.value) return 0
    return calcTimerTime(getActiveParentTimer.value)
  })
  const getCurrentChildTime = computed((): number => {
    if (!getActiveChildTimer.value) return 0
    return calcTimerTime(getActiveChildTimer.value)
  })

  /*
   * Insert timer
   */

  /**
   * Checks if a single time value is between any existing timers
   * @param data {time: Date, index: number} index will ignore a specific timer.
   * Useful if moving an already created timer.
   * @returns boolean
   */
  const timeOverlaps = (data: { date: Date; index?: number }) => {
    return timers.value.some((parent, index) => {
      //between any parent timers, return true, but if end time null (active timer)
      //then return false in this case, starting a new timer

      //If same index, don't bother comparing
      if (typeof data.index !== 'undefined' && index === data.index) {
        return false
      }
      if (parent.end_time === null) {
        return data.date.getTime() >= parent.start_time.getTime()
      }
      if (parent.end_time !== null) {
        return (
          data.date.getTime() > parent.start_time.getTime() &&
          data.date.getTime() < parent.end_time!.getTime()
        )
      }
    })
  }

  //Returns true any conflict
  //if Index provided, will skip that timer
  const timerConflict = (
    newTimes: {
      start_time: Timer['start_time']
      end_time: Timer['end_time']
      index?: number
    },
    timerRef: Timer[] | ChildTimers = timers.value
  ) => {
    return timerRef.some((timer, timerIndex) => {
      if (timer.end_time === null) return false
      if (typeof newTimes.index !== 'undefined' && newTimes.index == timerIndex) {
        return false
      }
      return (
        overlaps(newTimes, timer.start_time) ||
        overlaps(newTimes, timer.end_time) ||
        betweenParentTimes(newTimes, { start_time: timer.start_time, end_time: timer.end_time! })
      )
    })
  }

  const editParentStartEnd = (
    newTimes: {
      start_time: Timer['start_time']
      end_time: Timer['end_time']
    },
    parentIndex: number
  ) => {
    const parent = timers.value[parentIndex]
    if (
      !timerConflict({
        index: parentIndex,
        start_time: newTimes.start_time,
        end_time: newTimes.end_time,
      })
    ) {
      parent.start_time = newTimes.start_time
      parent.end_time = newTimes.end_time
      addTimerHistory(parent)
      sortTimers()
    }
  }

  const addTimerHistory = (timerRef: Timer | ChildTimer) => {
    if (!timerRef.data.history) {
      timerRef.data.history = {
        _created_at: new Date(),
        _staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
      }
    }
    timerRef.data.history._last_updated_at = new Date()
  }

  const editChildData = (
    parentIndex: number,
    childIndex: number,
    data: { [key: string]: MaybeRef<any> }
  ) => {
    const parent = timers.value[parentIndex]
    const child = parent.children[childIndex]
    for (const key in data) {
      child.data[key] = unref(data[key])
    }
  }
  const editChildStartEnd = (
    newTimes: {
      start_time: Timer['start_time']
      end_time: NonNullable<Timer['end_time']>
    },
    parentIndex: number,
    childIndex: number
  ) => {
    const parent = timers.value[parentIndex]
    const child = parent.children[childIndex]
    if (!timerConflict({ ...newTimes, index: childIndex }, parent.children)) {
      child.start_time = newTimes.start_time
      child.end_time = newTimes.end_time
      addTimerHistory(child)
      //for now, we don't need this
      //sort timers to get order
      // parent.children.sort((child1, child2) => {
      //   return child1.start_time.valueOf() - child2.start_time.valueOf()
      // })
    }
  }

  const getTimersForSync = (): SyncTimers => {
    const user = storeToRefs(useUserStore())
    //@ts-expect-error skip for now, todo: remove this line
    return mapTimersForSync(timers.value, user)
  }

  const getTimeSheet = (): Omit<SyncTimesheet, 'agree_legal' | 'claim_travel_override'> => {
    //server will check for this null value
    const { getExpensesByDate } = useExpenses()
    const { getJWT } = storeToRefs(useAuthenticateSAML())
    const staffing_user_id = getJWT.value.staffing_users_id
    const activities = getTimersForSync()
    const tsDate = new Date(activities[0].start_time)
    const now = new Date()
    return {
      staffing_user_id,
      date: formatISO(tsDate),
      submission_time: formatISO(now),
      activity_data: activities,
      expenses: getExpensesByDate(tsDate),
    }
  }

  const getIncompleteTimeSheet = computed(() => {
    if (timers.value.length < 1) return null
    const timesheet = { ...getTimeSheet() } as unknown as Timesheet
    //@ts-expect-error converting between timesheet types expected
    timesheet.timers = timesheet.activity_data
    //@ts-expect-error converting between timesheet types expected
    delete timesheet.activity_data
    timesheet.status = 'incomplete'
    timesheet.deniedReason = null
    timesheet.deniedDate = null
    timesheet.toBePaidDistance = 0
    timesheet.toBePaidHours = 0
    timesheet.time = 0
    //@ts-expect-error converting between timesheet types expected
    timesheet.expensesData = timesheet.expenses
    timesheet.expenses = 0
    timesheet.orders = 0
    timesheet.modifiedByRep = false
    timesheet.readyToSync = false

    return timesheet
  })

  return {
    resetStoreState,
    //@ts-expect-error this is ok
    timers: computed(() => timers.value),
    originalTimers: computed(() => originalTimers),
    stopActiveParentTimer,
    addParentTimer,
    removeParent,
    removeChildByDataField,
    getAllParentTimers: readonly(timers),
    getTimeSheet,
    getActiveParentTimer,
    storeActive,
    endParentTimer,
    editParentData,
    editParentStartTime,
    editParentEndTime,
    stopActiveChildTimer,
    editChildData,
    editChildStartTime,
    editChildEndTime,
    getActiveChildTimer,
    addChildTimer,
    clearAllTimers,
    breakActive,
    startBreak,
    endBreak,
    startDriving,
    endDriving,
    calcTimerTime,
    getTotalBreak,
    getTotalParentTimes,
    getTotalTime,
    getCurrentBreakTime,
    getCurrentParentTime,
    getCurrentChildTime,
    sortTimers,
    lockTimerEdits,
    unlockTimerEdits,
    isLocked,
    timerConflict,
    timeOverlaps,
    insertNewTimer,
    canEdit: {
      parentStart: canEditParentStart,
      parentEnd: canEditParentEnd,
      childStart: canEditChildStart,
      childEnd: canEditChildEnd,
    },
    setTimers,
    resetOriginalTimers,
    setOriginalTimers,
    editParentStartEnd,
    editChildStartEnd,
    getIncompleteTimeSheet,
    swappedTimers,
  }
})
/**
 * Tests if a set of start/end times is inbetween a timer
 * @param newTimes start/end dates (end can be null)
 * @param parentTimes start/end dates (end can be null)
 * @returns boolean
 */
export const betweenParentTimes = (
  newTimes: {
    start_time: Timer['start_time']
    end_time: Timer['end_time']
  },
  parentTimes: {
    start_time: Timer['start_time']
    end_time: Timer['end_time']
  }
): boolean => {
  if (!newTimes.end_time) {
    return false
  } else if (parentTimes.end_time) {
    return (
      stripSeconds(parentTimes.start_time) <= stripSeconds(newTimes.start_time) &&
      stripSeconds(newTimes.end_time) <= stripSeconds(parentTimes.end_time)
    )
  } else {
    //can put timer in front
    return false
  }
}
/**
 * Checks if two timestamps (start/end) overlap a date
 * @param newTimes Object of start/end time to be edited
 * @param parentTime Date
 * @returns Boolean
 */
export const overlaps = (
  newTimes: {
    start_time: Timer['start_time']
    end_time: Timer['end_time']
  },
  parentTime: NonNullable<Timer['start_time'] | Timer['end_time']>
): boolean => {
  if (!newTimes.end_time) {
    return false
  } else if (
    stripSeconds(newTimes.start_time) < stripSeconds(parentTime) &&
    stripSeconds(parentTime) < stripSeconds(newTimes.end_time)
  ) {
    return true
  } else return false
}
const stripSeconds = (date: Date) => {
  date.setSeconds(0)
  date.setMilliseconds(0)
  return date
}
const travelType = (field: 'departing' | 'destination') => (data: DeepReadonly<Timer>) => {
  const { t } = useI18n({ useScope: 'global' })
  const formOptions = (key: string) => t(`form.options.${key}`)
  if (data.data[field] === 'store') {
    return `${data.data[field + 'StoreName']} ${data.data[field + 'StoreNumber']}`
  } else {
    return formOptions(data.data[field])
  }
}
const toTravel = travelType('destination')
const fromTravel = travelType('departing')
export const formatTravelActivitesForTable = (activities: DeepReadonly<Timer[]>) => {
  const mappedAct = activities
    .filter((activity) => activity.type === 'travel')
    .map((activity) => {
      const date = useFormatDate(new Date(activity.start_time))
      return {
        date: `${date.weekday()}, ${date.month()} ${date.day()}`,
        to: toTravel(activity),
        from: fromTravel(activity),
        distance: activity.data.odometer_end - activity.data.odometer_start,
      }
    })
  return {
    meta: {
      columns: {
        date: {
          label: {
            en_US: 'Date',
            fr_CA: 'Date',
            es_MX: 'Date',
          },
          type: 'string',
        },
        from: {
          label: {
            en_US: 'From',
            fr_CA: 'From',
            es_MX: 'From',
          },
          type: 'string',
        },
        to: {
          label: {
            en_US: 'To',
            fr_CA: 'To',
            es_MX: 'To',
          },
          type: 'string',
        },
        distance: {
          label: {
            en_US: 'Distance',
            fr_CA: 'Distance',
            es_MX: 'Distance',
          },
          type: 'number',
        },
      },
    },
    data: mappedAct,
  }
}

/**Creates an incomplete timesheet based on the timers provided */
export const createIncompleteTimesheet = (timerData: Timer[], meta: { deviceDate: string }) => {
  const date = new Date(timerData[0].start_time)
  const { getExpensesByDate } = useExpenses()
  const timesheet: Timesheet = {
    // staffing_user_id: user.staffing_user_id,
    date: format(date, 'MM-dd-yyyy'),
    deviceDate: meta.deviceDate,
    timers: timerData,
    originalTimers: timerData,
    expensesData: getExpensesByDate(new Date(date)),
    status: 'incomplete',
    deniedReason: null,
    deniedDate: null,
    toBePaidDistance: 0,
    toBePaidHours: 0,
    time: 0,
    expenses: 0,
    orders: 0,
    modifiedByRep: false,
    readyToSync: false,
    agree_legal: null, //null is ok here - we will ask it later
    claim_travel_override: null, //null is ok here - we will ask it later
  }

  return timesheet
}
